import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import tradingService from '../services/tradingService';
import './TradeDetailsPage.css';

const isCanceledRequest = (err) => {
  if (!err) return false;
  return (
    err?.name === 'AbortError' ||
    err?.name === 'CanceledError' ||
    err?.code === 'ERR_CANCELED' ||
    String(err?.message || '').toLowerCase() === 'canceled'
  );
};

export default function TradeDetailsPage() {
  const { number, tradeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trade, setTrade] = useState(null);

  useEffect(() => {
    if (!number || !tradeId) return;

    const ac = new AbortController();
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await tradingService.accounts.trades.tradeDetails({
          number,
          tradeId,
          signal: ac.signal,
        });

        if (!alive) return;
        setTrade(res?.data ?? null);
      } catch (err) {
        if (!alive || isCanceledRequest(err) || ac.signal.aborted) return;
        setTrade(null);
        setError(err?.message || 'Failed to load trade details');
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [number, tradeId]);

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : String(value);
    return String(value);
  };

  const statusText = trade?.trade_status?.label ?? '—';
  const statusClass = useMemo(() => {
    const id = trade?.trade_status?.trade_status_id;
    if (id === 1) return 'trade-status trade-status--created';
    if (id === 2) return 'trade-status trade-status--failed';
    if (id === 3) return 'trade-status trade-status--executed';
    return 'trade-status trade-status--unknown';
  }, [trade]);

  return (
    <div className="card">
      <div className="trade-details__header">
        <div>
          <h1 className="trade-details__title">Trade details</h1>
          <div className="trade-details__meta">
            Trade <code>{tradeId}</code>
          </div>
        </div>

        <div className="trade-details__actions">
          <button type="button" className="account-details__btn" onClick={() => navigate(`/my/accounts/${number}`)}>
            Back to account
          </button>
        </div>
      </div>

      {error ? <p className="account-details__error">{error}</p> : null}
      {loading ? <p>Loading…</p> : null}

      {!loading && trade ? (
        <>
          <section className="account-details__summary">
            <div className="trade-details__summary-top">
              <div>
                <div className="account-details__summary-kicker">Overview</div>
                <div className="trade-details__symbol">
                  <code>{trade?.symbol?.name ?? '—'}</code>
                  <span className={statusClass}>{statusText}</span>
                </div>
              </div>

              <div className="trade-details__direction" title={trade?.direction?.description ?? ''}>
                {trade?.direction?.name ?? '—'}
              </div>
            </div>

            <div className="trade-details__grid">
              <div className="trade-details__kv">
                <div className="trade-details__k">Order id</div>
                <div className="trade-details__v">
                  <code>{trade?.trade_order_id ?? '—'}</code>
                </div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Client order id</div>
                <div className="trade-details__v">
                  <code>{trade?.client_order_id ?? '—'}</code>
                </div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Exchange order id</div>
                <div className="trade-details__v">
                  <code>{trade?.exchange_order_id ?? '—'}</code>
                </div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Created</div>
                <div className="trade-details__v">{formatDateTime(trade?.date_created)}</div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Updated</div>
                <div className="trade-details__v">{formatDateTime(trade?.date_updated)}</div>
              </div>
            </div>
          </section>

          <section className="account-details__section">
            <div className="account-details__section-header">
              <div>
                <div className="account-details__section-title">Execution</div>
                <div className="account-details__section-subtitle">Quantities and prices returned by the API.</div>
              </div>
            </div>

            <div className="trade-details__grid">
              <div className="trade-details__kv">
                <div className="trade-details__k">Quantity</div>
                <div className="trade-details__v">{formatNumber(trade?.quantity)}</div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Quote quantity</div>
                <div className="trade-details__v">{formatNumber(trade?.quote_quantity)}</div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Executed quantity</div>
                <div className="trade-details__v">{formatNumber(trade?.executed_quantity)}</div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Executed price</div>
                <div className="trade-details__v">{formatNumber(trade?.executed_price)}</div>
              </div>

              <div className="trade-details__kv">
                <div className="trade-details__k">Strategy position %</div>
                <div className="trade-details__v">{formatNumber(trade?.strategy_position_percent)}</div>
              </div>
            </div>
          </section>

          <section className="account-details__section">
            <div className="account-details__section-header">
              <div>
                <div className="account-details__section-title">Notes</div>
                <div className="account-details__section-subtitle">User and exchange comments.</div>
              </div>
            </div>

            <div className="trade-details__notes">
              <div className="trade-details__note">
                <div className="trade-details__k">Comment</div>
                <div className="trade-details__v">{trade?.comment ?? '—'}</div>
              </div>

              <div className="trade-details__note">
                <div className="trade-details__k">Exchange comment</div>
                <div className="trade-details__v">{trade?.comment_exchange ?? '—'}</div>
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
